"use client";
import { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [language, setLanguage] = useState("ml");

  const [theme, setTheme] = useState("dark");

  // NEW — Accessibility Modes
  const [accessibilityMode, setAccessibilityMode] = useState("cursor");

  /*
    Modes:
    cursor → default cursor
    bigcursor → big circle cursor
    mask → reading mask
    guide → reading guide line
  */

  // Cycle modes
  const toggleAccessibilityMode = () => {
    const modes = ["cursor", "bigcursor", "mask", "guide"];
    const currentIndex = modes.indexOf(accessibilityMode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    setAccessibilityMode(nextMode);

    // Clear all before applying next
    document.body.classList.remove("big-cursor", "reading-mask", "reading-guide");

    if (nextMode === "bigcursor") {
      document.body.classList.add("big-cursor");
    }
    if (nextMode === "mask") {
      document.body.classList.add("reading-mask");
    }
    if (nextMode === "guide") {
      document.body.classList.add("reading-guide");
    }
  };

  // Move big cursor
  useEffect(() => {
    if (accessibilityMode !== "bigcursor") return;

    const move = (e) => {
      document.body.style.setProperty("--x", e.clientX + "px");
      document.body.style.setProperty("--y", e.clientY + "px");
    };

    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [accessibilityMode]);

  // Reading mask movement
  useEffect(() => {
    if (accessibilityMode !== "mask") return;

    const move = (e) => {
      document.body.style.setProperty("--maskY", e.clientY - 60 + "px");
    };

    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [accessibilityMode]);

  // Reading guide movement
  useEffect(() => {
    if (accessibilityMode !== "guide") return;

    const move = (e) => {
      document.body.style.setProperty("--guideY", e.clientY + "px");
    };

    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [accessibilityMode]);

  // Load theme on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedTheme = localStorage.getItem("theme");
    const storedLang = localStorage.getItem("language");

    if (storedTheme) {
      setTheme(storedTheme);
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(storedTheme);
    } else {
      document.documentElement.classList.add("dark");
    }

    if (storedLang) {
      setLanguage(storedLang);
      document.documentElement.setAttribute("lang", storedLang);
      document.documentElement.classList.add(`lang-${storedLang}`);
    }
  }, []);

  // Change language
  useEffect(() => {
    document.documentElement.classList.remove("lang-en", "lang-ml");
    document.documentElement.classList.add(`lang-${language}`);
    document.documentElement.setAttribute("lang", language);
    if (typeof window !== "undefined") {
      localStorage.setItem("language", language);
    }
  }, [language]);

  // THEME TOGGLE
  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const toggleLanguage = () => {
    const newLang = language === "en" ? "ml" : "en";
    setLanguage(newLang);
  };

  const [hideImages, setHideImages] = useState(false);

  const toggleHideImages = () => {
    const newValue = !hideImages;
    setHideImages(newValue);

    if (newValue) {
      document.body.classList.add("no-image-mode");
    } else {
      document.body.classList.remove("no-image-mode");
    }
  };

  // 1. Contrast
  const [contrastMode, setContrastMode] = useState("normal");
  const toggleContrast = () => {
    const options = ["normal", "dark", "light", "invert"];
    const idx = options.indexOf(contrastMode);
    const next = options[(idx + 1) % options.length];
    setContrastMode(next);

    document.body.classList.remove(
      "contrast-dark",
      "contrast-light",
      "contrast-invert"
    );

    if (next === "dark") document.body.classList.add("contrast-dark");
    if (next === "light") document.body.classList.add("contrast-light");
    if (next === "invert") document.body.classList.add("contrast-invert");
  };

  // 2. Text Size
  const [textSize, setTextSize] = useState("normal");
  const toggleTextSize = () => {
    const sizes = ["normal", "medium", "large", "xlarge"];
    const idx = sizes.indexOf(textSize);
    const next = sizes[(idx + 1) % sizes.length];
    setTextSize(next);

    document.body.classList.remove(
      "text-medium",
      "text-large",
      "text-xlarge"
    );

    if (next === "medium") document.body.classList.add("text-medium");
    if (next === "large") document.body.classList.add("text-large");
    if (next === "xlarge") document.body.classList.add("text-xlarge");
  };

  // 3. Letter Spacing
  const [spacing, setSpacing] = useState("normal");
  const toggleSpacing = () => {
    const opt = ["normal", "medium", "wide"];
    const idx = opt.indexOf(spacing);
    const next = opt[(idx + 1) % opt.length];
    setSpacing(next);

    document.body.classList.remove("spacing-medium", "spacing-wide");

    if (next === "medium") document.body.classList.add("spacing-medium");
    if (next === "wide") document.body.classList.add("spacing-wide");
  };

  // 4. Animation Pause
  const [pauseAnim, setPauseAnim] = useState(false);
  const togglePauseAnimation = () => {
    const next = !pauseAnim;
    setPauseAnim(next);

    if (next) document.body.classList.add("anim-paused");
    else document.body.classList.remove("anim-paused");
  };

  // 5. Saturation
  const [saturation, setSaturation] = useState("normal");
  const toggleSaturation = () => {
    const opt = ["normal", "low", "high", "desaturate"];
    const idx = opt.indexOf(saturation);
    const next = opt[(idx + 1) % opt.length];
    setSaturation(next);

    document.body.classList.remove(
      "sat-low",
      "sat-high",
      "sat-desaturate"
    );

    if (next === "low") document.body.classList.add("sat-low");
    if (next === "high") document.body.classList.add("sat-high");
    if (next === "desaturate") document.body.classList.add("sat-desaturate");
  };

  // 6. Line Height
  const [lineHeight, setLineHeight] = useState("normal");
  const toggleLineHeight = () => {
    const values = ["normal", "1.5", "1.7", "2"];
    const idx = values.indexOf(lineHeight);
    const next = values[(idx + 1) % values.length];
    setLineHeight(next);

    document.body.classList.remove("lh-15", "lh-17", "lh-20");

    if (next === "1.5") document.body.classList.add("lh-15");
    if (next === "1.7") document.body.classList.add("lh-17");
    if (next === "2") document.body.classList.add("lh-20");
  };

  // 7. Text Alignment
  const [align, setAlign] = useState("left");
  const toggleAlign = () => {
    const options = ["left", "center", "right"];
    const idx = options.indexOf(align);
    const next = options[(idx + 1) % options.length];
    setAlign(next);

    document.body.classList.remove("align-left", "align-center", "align-right");
    document.body.classList.add(`align-${next}`);
  };



  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        language,
        toggleLanguage,
        accessibilityMode,
        toggleAccessibilityMode,
        hideImages,
        toggleHideImages,
        contrastMode,
        toggleContrast,
        textSize,
        toggleTextSize,
        spacing,
        toggleSpacing,
        pauseAnim,
        togglePauseAnimation,
        saturation,
        toggleSaturation,
        lineHeight,
        toggleLineHeight,
        align,
        toggleAlign,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
